import React from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';

/**
 * ThemeSwitch — custom light/dark theme toggle switch component.
 * 
 * Uses its own icon and color logic (hardcoded values).
 * Supports props: checked, onChange, sx (for external styling).
 * 
 * @component
 * @example
 * // Usage in Layout
 * <ThemeSwitch checked={themeType === 'dark'} onChange={handleThemeToggle} sx={{ width: 200 }} />
 * 
 * @param {Object} props
 * @param {boolean} props.checked - Current state (true = dark theme)
 * @param {function} props.onChange - Change handler
 * @param {object} [props.sx] - Additional styles (MUI sx)
 */
const SwitchRoot = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 36,
  background: '#1a202c',
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  cursor: 'pointer',
  padding: 4,
  boxSizing: 'border-box',
}));

const Thumb = styled(Box)(({ checked }) => ({
  width: '50%',
  height: 28,
  borderRadius: '12px',
  background: '#d1d1d1',
  position: 'absolute',
  top: 4,
  left: checked ? 'calc(50% - 4px)' : 4,
  transition: 'left 0.3s',
  boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
  zIndex: 2,
}));

const IconBox = styled(Box)(({ active }) => ({
  width: '50%',
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: active ? '#1a202c' : '#d1d1d1',
  zIndex: 3,
}));

const ThemeSwitch = ({ checked, onChange, sx }) => {
  return (
    <SwitchRoot
      className="theme-switch"
      onClick={() => onChange && onChange({ target: { checked: !checked } })}
      sx={sx}
    >
      <IconBox active={!checked}>
        <LightModeIcon sx={{ fontSize: 20 }} />
      </IconBox>
      <IconBox active={checked} sx={{ position: 'absolute', right: 4 }}>
        <DarkModeIcon sx={{ fontSize: 20 }} />
      </IconBox>
      <Thumb checked={checked} />
    </SwitchRoot>
  );
};

export default ThemeSwitch;
